/* eslint-disable no-unused-vars, no-undef */
/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Plus, Trash2, AlertCircle, Lock, Pencil
} from 'lucide-react';
import {
    apiManagerService
} from 'shared/api/services/BEX/apiManager.service';
import FieldHelp from '../components/FieldHelp';
import {
    cn
} from 'shared/lib/utils';
import ApiPicker from '../inline-pickers/ApiPicker';
import {
    StepCommonAdvanced, StepCommonInlineFields, StepOelEditor,
    useProcedureStepIds
} from './_shared';
import {
    Skeleton
} from 'shared/components/ui/skeleton';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas, StepFieldZone, llmFocusInputClass, INDIGO_ZONE_PANEL
} from './llmStepLayout';

/**
 * InlineAPIStep — kind:"api".
 * Schema-driven editor: when an API is picked, we load its `paramsDef`
 * (path / query / header) + `input_schema` (body) and render one row per
 * declared param with an OelEditor next to it. Users can also add ad-hoc
 * params for path/query/header at build time.
 *
 * Storage: each group (path_params / query_params / headers / body) is
 * stored on `step.inputs.*` as an OEL DICT LITERAL string of the form
 * `{'k1': <oel>, 'k2': <oel>}`. Project O's OEL evaluator already supports
 * dict literals with @refs as values, so this round-trips cleanly.
 *
 * If a previously-saved value isn't parseable as a flat dict literal, we
 * preserve the raw expression and show a "Complex expression in use" chip
 * with a button to discard it and start structured.
 */
export default function InlineAPIStep({
    step, onPatchStep, oelContext
}) {
    const {
        oragID, subOragID
    } = useParams();
    const inputs = step.inputs || {};
    const apiId = step.api_id || '';

    const [apis, setApis] = useState([]);
    const [loadingApi, setLoadingApi] = useState(false);
    const [loadErr, setLoadErr] = useState(null);
    const [editTrigger, setEditTrigger] = useState(0);

    // Load full list per sub-org. Re-runs after `editTrigger` ticks so the new
    // schema lights up immediately when the user edits the API in-place.
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        let cancelled = false;
        if (!oragID || !subOragID) return undefined;
        setLoadingApi(true);
        setLoadErr(null);
        apiManagerService.listApis(oragID, subOragID)
            .then((res) => {
                if (cancelled) return;
                const arr = res?.data || res || [];
                setApis(Array.isArray(arr) ? arr : []);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadErr(err?.message || 'Failed to load API records.');
            })
            .finally(() => { if (!cancelled) setLoadingApi(false); });
        return () => { cancelled = true; };
    }, [oragID, subOragID, reloadKey]);

    const api = useMemo(() => apis.find((a) => a._id === apiId) || null, [apis, apiId]);

    // The API editor stores rows as {key, value} with no `in` field. The
    // opcode step needs {name, in, type} to render structured rows. Normalize
    // here so user-defined params (and the API's static headers) light up as
    // schema-driven rows with prefilled keys.
    const apiDefs = useMemo(() => normalizeApiDefs(api), [api]);

    // Inputs loaded from saved JSON may have object values instead of OEL dict
    // literal strings (e.g. body: { message: "@input.text" } instead of
    // "{'message': @input.text}"). Coerce them so child editors never receive
    // a plain object as their `value` prop (which crashes React render #31).
    const normalizedInputs = useMemo(() => {
        const raw = step.inputs || {};
        const coerce = (v) => {
            if (v == null || typeof v === 'string') return v;
            if (typeof v === 'object') return serializeOelDict(
                Object.fromEntries(Object.entries(v).map(([k, val]) => [k, String(val)]))
            );
            return String(v);
        };
        return {
            ...raw,
            body: coerce(raw.body),
            path_params: coerce(raw.path_params),
            query_params: coerce(raw.query_params),
            headers: coerce(raw.headers),
        };
    }, [step.inputs]);

    const patch = (p) => onPatchStep({
        ...step,
        ...p
    });
    const patchInputs = (p) => patch({
        inputs: {
            ...inputs,
            ...p
        }
    });

    const stepIdOptions = useProcedureStepIds(oelContext);
    const inputClass = cn('h-8 text-[12.5px]', llmFocusInputClass);

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="API"
                description="Pick an API record from Library — its method, URL, and schemas drive the parameter editors below."
            >
                <FieldHelp>
                    Use <strong>Change</strong> to swap APIs or <strong>Edit</strong> to
                    update schemas in Library → APIs.
                </FieldHelp>
                {loadingApi ? (
                    <ApiSectionSkeleton />
                ) : (
                    <>
                        <ApiPicker
                            apiId={apiId}
                            selectedApi={api}
                            onChange={(picked) => {
                                patch({
                                    api_id: picked?._id || ''
                                });
                                setReloadKey((n) => n + 1);
                                window.dispatchEvent(new CustomEvent('bex:api-saved', {
                                    detail: {
                                        _id: picked?._id
                                    }
                                }));
                            }}
                            editTrigger={editTrigger}
                        />
                        {loadErr && (
                            <div className="flex items-start gap-2 text-[11px] text-destructive">
                                <AlertCircle className="mt-0.5 h-3 w-3" /> {loadErr}
                            </div>
                        )}
                        {api && (
                            <div className="rounded-md border border-dashed bg-white/60 px-3 py-2 text-[11.5px]">
                                <span className="font-mono font-semibold text-primary">{api.method || '—'}</span>
                                <span className="font-mono text-muted-foreground"> {api.url || '—'}</span>
                            </div>
                        )}
                    </>
                )}
            </LlmStepZone>

            {!apiId && (
                <div className="rounded-md border border-dashed bg-white/60 px-3 py-3 text-[11.5px] text-muted-foreground">
                    Pick an API above — the parameter editors light up once the schema is loaded.
                </div>
            )}

            {apiId && (
                <>
                    <ParamGroupEditor
                        label="Path Parameters"
                        hint="substitutes {key} segments in the URL"
                        defs={apiDefs.filter((p) => p.in === 'path')}
                        value={normalizedInputs.path_params || ''}
                        onChange={(v) => patchInputs({
                            path_params: v
                        })}
                        oelContext={oelContext}
                        allowAdd
                        addPlaceholder="path-param-name"
                    />

                    <ParamGroupEditor
                        label="Query Parameters"
                        hint="appended to the URL"
                        defs={apiDefs.filter((p) => p.in === 'query')}
                        value={normalizedInputs.query_params || ''}
                        onChange={(v) => patchInputs({
                            query_params: v
                        })}
                        oelContext={oelContext}
                        allowAdd
                        addPlaceholder="query-param-name"
                    />

                    <ParamGroupEditor
                        label="Headers"
                        hint="merged with the API's static headers"
                        defs={apiDefs.filter((p) => p.in === 'header')}
                        value={normalizedInputs.headers || ''}
                        onChange={(v) => patchInputs({
                            headers: v
                        })}
                        oelContext={oelContext}
                        allowAdd
                        addPlaceholder="X-Header-Name"
                    />

                    <BodyEditor
                        api={api}
                        value={normalizedInputs.body || ''}
                        onChange={(v) => patchInputs({
                            body: v
                        })}
                        oelContext={oelContext}
                    />

                    <details className={cn('rounded-xl border border-indigo-100/80 px-3 py-2.5', INDIGO_ZONE_PANEL)}>
                        <summary className="cursor-pointer text-xs font-semibold capitalize text-muted-foreground">
                            Advanced (auth · timeout)
                        </summary>
                        <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <LlmLabeled label="Auth (OEL)" help="Overrides the API's saved auth — leave empty to use it">
                                <StepOelEditor
                                    value={normalizedInputs.auth || ''}
                                    onChange={(v) => patchInputs({ auth: v })}
                                    ctx={oelContext}
                                    placeholder="optional"
                                    ariaLabel="API auth override"
                                />
                            </LlmLabeled>
                            <LlmLabeled label="Request timeout (s)" help="Overrides the API's saved timeout">
                                <Input
                                    type="number"
                                    value={inputs.request_timeout_s ?? ''}
                                    onChange={(e) => patchInputs({
                                        request_timeout_s: e.target.value === '' ? undefined : Number(e.target.value)
                                    })}
                                    placeholder="seconds"
                                    className={inputClass}
                                />
                            </LlmLabeled>
                        </div>
                    </details>

                    {api && api.output_schema ? (
                        <div className="rounded-md border bg-muted/10 px-3 py-2 text-[11px] text-muted-foreground">
                            <Lock className="mr-1 inline h-3 w-3" />
                            <b>Response shape known.</b> Other steps can pick nested keys from
                            this step's output via the <span className="font-mono">@</span> menu —
                            paths are derived from the API's <span className="font-mono">output_schema</span>.
                        </div>
                    ) : api ? (
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[11.5px] text-amber-900">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <div className="flex-1 space-y-1">
                                <div>
                                    <b>This API has no response schema declared.</b> Downstream
                                    steps will only see <span className="font-mono">@{step.id}.output.data</span> as
                                    a generic object — they won't be able to drill into nested
                                    keys like <span className="font-mono">.task.owner.email</span> from
                                    the <span className="font-mono">@</span> menu.
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
                                    onClick={() => setEditTrigger((n) => n + 1)}
                                >
                                    <Pencil className="mr-1 h-3 w-3" /> Edit API to add schemas
                                </Button>
                            </div>
                        </div>
                    ) : null}

                    {api && (!api.paramsDef || api.paramsDef.length === 0) && !api.input_schema && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[11.5px] text-amber-900">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <div className="flex-1 space-y-1">
                                <div>
                                    <b>This API has no input schema declared.</b> Path / Query / Header
                                    parameter rows above are showing only ad-hoc entries you've added
                                    here — declare a <span className="font-mono">paramsDef</span> or
                                    a body <span className="font-mono">input_schema</span> on the
                                    API itself for typed prompts and validation.
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
                                    onClick={() => setEditTrigger((n) => n + 1)}
                                >
                                    <Pencil className="mr-1 h-3 w-3" /> Edit API to add schemas
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}

/** Skeleton placeholder for the API picker chip while the library list loads. */
function ApiSectionSkeleton() {
    return (
        <div className="space-y-3" aria-busy="true" aria-label="Loading API">
            <div className="flex items-center gap-3 rounded-lg border border-indigo-100/60 bg-white/90 p-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-full max-w-md" />
                </div>
                <div className="flex shrink-0 gap-2">
                    <Skeleton className="h-8 w-[4.5rem] rounded-md" />
                    <Skeleton className="h-8 w-14 rounded-md" />
                    <Skeleton className="h-8 w-[4.5rem] rounded-md" />
                </div>
            </div>
            <Skeleton className="h-3 w-[85%] max-w-lg" />
            <Skeleton className="h-9 w-full rounded-md border border-dashed border-indigo-100/60" />
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  ParamGroupEditor — schema-driven row list per param group      */
/* ─────────────────────────────────────────────────────────────── */
function ParamGroupEditor({
    label, hint, defs, value, onChange, oelContext, allowAdd, addPlaceholder
}) {
    // Parse current OEL dict literal into a structured map. If parsing fails,
    // we keep the raw value and show the "complex expression" affordance.
    const parsed = useMemo(() => parseOelDict(value), [value]);
    const map = parsed.ok ? parsed.map : {};
    const isComplex = !parsed.ok && (value || '').trim().length > 0;

    const definedNames = (defs || []).map((d) => d.name).filter(Boolean);

    // Ad-hoc keys can come from two places:
    //   (1) the serialized OEL value (persisted, with a non-empty expression), or
    //   (2) local state — the user just clicked "Add custom …" but hasn't typed
    //       a value yet. We must track these locally because serializeOelDict
    //       drops entries with empty values, so an empty stub can't round-trip
    //       through `value` / `onChange`.
    const [pendingAdhocKeys, setPendingAdhocKeys] = useState([]);
    const adhocKeysFromValue = Object.keys(map).filter((k) => !definedNames.includes(k));
    const adhocKeys = Array.from(new Set([...adhocKeysFromValue, ...pendingAdhocKeys]))
        .filter((k) => !definedNames.includes(k));
    const allKeys = [...definedNames, ...adhocKeys];

    function setKey(k, v) {
        const next = {
            ...map
        };
        if (v === undefined) {
            delete next[k];
            setPendingAdhocKeys((prev) => prev.filter((x) => x !== k));
        } else {
            next[k] = v;
        }
        const serialized = serializeOelDict(next);
        onChange(serialized);
    }
    function addAdhoc() {
        const name = window.prompt(`Add ${addPlaceholder}`)?.trim();
        if (!name) return;
        if (allKeys.includes(name)) return;
        setPendingAdhocKeys((prev) => [...prev, name]);
    }
    function discardComplex() {
        if (!window.confirm('Discard the existing expression and switch to structured rows?')) return;
        onChange('');
    }

    if (defs.length === 0 && allKeys.length === 0 && !allowAdd) return null;

    // No schema declared on the API → fall through to a raw OEL editor
    // (mirrors the Body section's no-schema behavior). Lets the user write
    // the whole dict — including LLM-output / @input refs — as one OEL
    // expression instead of going through the prompt-and-row flow.
    if (defs.length === 0) {
        return (
            <StepFieldZone title={label} hint={`Raw OEL — ${hint}`} variant="arguments">
                <StepOelEditor
                    value={value || ''}
                    onChange={onChange}
                    ctx={oelContext}
                    placeholder={`{'${addPlaceholder}': @input.field}`}
                    ariaLabel={`${label} expression`}
                    multiline
                />
            </StepFieldZone>
        );
    }

    return (
        <StepFieldZone title={label} hint={hint} variant="arguments">
            {isComplex && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                    <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <div className="flex-1">
                        Existing expression is too complex for the structured editor.
                        Your structured edits below will overwrite it on save.
                        <Button type="button" variant="link" size="sm" className="h-auto px-1 py-0 text-[11px] text-amber-900 underline"
                            onClick={discardComplex}>
                            Discard &amp; start structured
                        </Button>
                    </div>
                </div>
            )}

            {defs.length === 0 && adhocKeys.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/10 px-3 py-2 text-[11px] text-muted-foreground">
                    No {label.toLowerCase()} declared on this API.
                    {allowAdd && <> Click <b>Add</b> below to provide one anyway.</>}
                </div>
            )}

            {allKeys.map((k) => {
                const def = defs.find((d) => d.name === k);
                const isAdhoc = !def;
                return (
                    <div key={k} className="grid grid-cols-[200px_1fr_auto] items-start gap-2">
                        <div className="pt-1.5">
                            <div className="font-mono text-[12.5px] text-foreground">{k}</div>
                            <div className="flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                                {def?.type && (
                                    <span className="rounded bg-muted px-1 font-semibold uppercase">{def.type}</span>
                                )}
                                {def?.required && (
                                    <span className="rounded bg-rose-100 px-1 font-semibold text-rose-800 uppercase">required</span>
                                )}
                                {!def?.required && def && (
                                    <span className="text-muted-foreground">optional</span>
                                )}
                                {isAdhoc && (
                                    <span className="rounded bg-blue-100 px-1 font-semibold text-blue-800 uppercase">ad-hoc</span>
                                )}
                                {def?.default !== undefined && (
                                    <span className="font-mono">default: {String(def.default)}</span>
                                )}
                            </div>
                            {def?.description && (
                                <div className="text-[10.5px] text-muted-foreground">{def.description}</div>
                            )}
                        </div>
                        <StepOelEditor
                            value={map[k] || ''}
                            onChange={(v) => setKey(k, v)}
                            ctx={oelContext}
                            placeholder={def?.default !== undefined ? `${def.default} (default)` : '@input.field'}
                            ariaLabel={`${label} ${k}`}
                            compact
                            expectedType={def?.type}
                        />
                        {isAdhoc && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setKey(k, undefined)}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                title="Remove ad-hoc parameter"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            })}

            {allowAdd && (
                <Button type="button" variant="outline" size="sm" onClick={addAdhoc}>
                    <Plus className="mr-1 h-3 w-3" /> Add custom {label.toLowerCase().replace(/s$/, '')}
                </Button>
            )}
        </StepFieldZone>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  BodyEditor — schema-driven if input_schema declares body shape */
/* ─────────────────────────────────────────────────────────────── */
function BodyEditor({
    api, value, onChange, oelContext
}) {
    // Try to extract a body shape: input_schema.properties.body.properties OR
    // (loose) the top-level properties of input_schema.
    const bodyShape = useMemo(() => extractBodyShape(api?.input_schema), [api]);

    if (!bodyShape) {
        return (
            <StepFieldZone title="Body" hint="Raw OEL — schema not declared" variant="arguments">
                <StepOelEditor
                    value={value || ''}
                    onChange={onChange}
                    ctx={oelContext}
                    placeholder="{'amount': @input.refund_amount}"
                    ariaLabel="API body expression"
                    multiline
                />
            </StepFieldZone>
        );
    }

    // Schema-driven: one row per top-level property
    const parsed = parseOelDict(value);
    const map = parsed.ok ? parsed.map : {};
    const isComplex = !parsed.ok && (value || '').trim().length > 0;

    const props = Object.entries(bodyShape.properties || {});
    const required = bodyShape.required || [];
    const adhoc = Object.keys(map).filter((k) => !props.find(([n]) => n === k));

    function setKey(k, v) {
        const next = {
            ...map
        };
        if (v === undefined) delete next[k];
        else next[k] = v;
        onChange(serializeOelDict(next));
    }
    function discardComplex() {
        if (!window.confirm('Discard the existing expression and switch to structured rows?')) return;
        onChange('');
    }

    return (
        <StepFieldZone
            title="Body"
            hint={`Schema-driven · ${props.length} field${props.length === 1 ? '' : 's'}`}
            variant="arguments"
        >
            {isComplex && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                    <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <div className="flex-1">
                        Existing body expression is complex. Edits below will overwrite it.
                        <Button type="button" variant="link" size="sm" className="h-auto px-1 py-0 text-[11px] text-amber-900 underline"
                            onClick={discardComplex}>
                            Discard &amp; start structured
                        </Button>
                    </div>
                </div>
            )}

            {props.map(([k, schema]) => (
                <div key={k} className="grid grid-cols-[200px_1fr] items-start gap-2">
                    <div className="pt-1.5">
                        <div className="font-mono text-[12.5px]">{k}</div>
                        <div className="flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                            <span className="rounded bg-muted px-1 font-semibold uppercase">{schema?.type || 'any'}</span>
                            {required.includes(k) && (
                                <span className="rounded bg-rose-100 px-1 font-semibold text-rose-800 uppercase">required</span>
                            )}
                            {schema?.format && (
                                <span className="font-mono">format: {schema.format}</span>
                            )}
                        </div>
                        {schema?.description && (
                            <div className="text-[10.5px] text-muted-foreground">{schema.description}</div>
                        )}
                    </div>
                    <StepOelEditor
                        value={map[k] || ''}
                        onChange={(v) => setKey(k, v)}
                        ctx={oelContext}
                        placeholder={schema?.default !== undefined ? `${schema.default} (default)` : '@input.field'}
                        ariaLabel={`Body ${k}`}
                        compact
                        expectedType={schema?.type}
                    />
                </div>
            ))}

            {adhoc.length > 0 && (
                <div className="rounded-md border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-blue-900">
                        Ad-hoc body fields (not in schema)
                    </div>
                    {adhoc.map((k) => (
                        <div key={k} className="mt-1.5 grid grid-cols-[200px_1fr_auto] items-start gap-2">
                            <div className="pt-1.5 font-mono text-[12.5px]">{k}</div>
                            <StepOelEditor
                                value={map[k] || ''}
                                onChange={(v) => setKey(k, v)}
                                ctx={oelContext}
                                placeholder="@input.field"
                                compact
                            />
                            <Button type="button" variant="ghost" size="sm"
                                onClick={() => setKey(k, undefined)}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </StepFieldZone>
    );
}

/**
 * Normalize the API record's params into a single `[{name, in, type, default, …}]`
 * array suitable for ParamGroupEditor.
 *
 * Sources we merge:
 *   • api.paramsDef — may use the canonical `{name, in, type, …}` shape OR the
 *     legacy `{key, value}` shape from the API editor's "Query Params" tab
 *     (treated as `in: 'query'`, with `value` as the default seed).
 *   • api.headers — map of static headers declared on the API; surfaced as
 *     `in: 'header'` defs so the user gets structured rows for them too.
 */
function normalizeApiDefs(api) {
    const out = [];
    const params = Array.isArray(api?.paramsDef) ? api.paramsDef : [];
    for (const p of params) {
        if (!p) continue;
        if (typeof p === 'string') {
            out.push({ name: p, in: 'query', type: 'string' });
        } else if (p.name) {
            out.push({ ...p, in: p.in || 'query', type: p.type || 'string' });
        } else if (p.key) {
            out.push({
                name: p.key,
                in: p.in || 'query',
                type: p.type || 'string',
                default: p.value,
            });
        }
    }
    const headers = api?.headers && typeof api.headers === 'object' ? api.headers : null;
    if (headers) {
        for (const [k, v] of Object.entries(headers)) {
            if (!k || out.find((d) => d.in === 'header' && d.name === k)) continue;
            out.push({ name: k, in: 'header', type: 'string', default: v });
        }
    }
    return out;
}

function extractBodyShape(schema) {
    if (!schema || typeof schema !== 'object') return null;
    // Conventional shape: input_schema = { type: 'object', properties: { body: { type: 'object', properties: {...} } } }
    if (schema.properties?.body?.properties) {
        return schema.properties.body;
    }
    // Looser: input_schema is itself an object schema and has properties (treat top-level as body)
    if (schema.type === 'object' && schema.properties && Object.keys(schema.properties).length > 0) {
        // Skip if all top-level props look like {path_params, query_params, body, headers}
        const reservedSet = new Set(['path_params', 'query_params', 'headers', 'body']);
        const propKeys = Object.keys(schema.properties);
        if (propKeys.every((k) => reservedSet.has(k))) return null;
        return schema;
    }
    return null;
}

/* ─────────────────────────────────────────────────────────────── */
/*  OEL dict literal helpers                                       */
/* ─────────────────────────────────────────────────────────────── */
/**
 * Parse a string like  `{'k1': @x, 'k2': @y + 1}`  into  `{ k1: '@x', k2: '@y + 1' }`.
 * Returns `{ ok: false }` if the string isn't a flat dict literal we understand.
 */
function parseOelDict(s) {
    if (s == null) return {
        ok: true,
        map: {}
    };
    const str = String(s).trim();
    if (str === '') return {
        ok: true,
        map: {}
    };
    if (!str.startsWith('{') || !str.endsWith('}')) return {
        ok: false
    };

    const body = str.slice(1, -1).trim();
    if (body === '') return {
        ok: true,
        map: {}
    };

    // Walk character by character; split entries by top-level commas; split key/value by top-level colon.
    const entries = splitTopLevel(body, ',');
    const map = {};
    for (const e of entries) {
        const trimmed = e.trim();
        if (!trimmed) continue;
        const colonIdx = findTopLevelColon(trimmed);
        if (colonIdx < 0) return {
            ok: false
        };
        const rawKey = trimmed.slice(0, colonIdx).trim();
        const rawVal = trimmed.slice(colonIdx + 1).trim();
        const keyMatch = /^['"]([A-Za-z_][A-Za-z0-9_-]*)['"]$/.exec(rawKey);
        if (!keyMatch) return {
            ok: false
        };
        map[keyMatch[1]] = rawVal;
    }
    return {
        ok: true,
        map
    };
}

function splitTopLevel(s, sep) {
    const out = [];
    let depth = 0, inStr = false;
    let buf = '';
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (c === '\\' && inStr && i + 1 < s.length) { buf += c + s[i + 1]; i++; continue; }
        if (c === "'") inStr = !inStr;
        else if (!inStr && (c === '{' || c === '[' || c === '(')) depth++;
        else if (!inStr && (c === '}' || c === ']' || c === ')')) depth--;
        else if (!inStr && depth === 0 && c === sep) {
            out.push(buf);
            buf = '';
            continue;
        }
        buf += c;
    }
    if (buf) out.push(buf);
    return out;
}

function findTopLevelColon(s) {
    let depth = 0, inStr = false;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (c === '\\' && inStr && i + 1 < s.length) { i++; continue; }
        if (c === "'") { inStr = !inStr; continue; }
        if (!inStr && (c === '{' || c === '[' || c === '(')) depth++;
        else if (!inStr && (c === '}' || c === ']' || c === ')')) depth--;
        else if (!inStr && depth === 0 && c === ':') return i;
    }
    return -1;
}

function serializeOelDict(map) {
    const entries = Object.entries(map || {}).filter(([, v]) => v !== undefined && String(v).trim() !== '');
    if (entries.length === 0) return '';
    const body = entries.map(([k, v]) => `'${k}': ${v}`).join(', ');
    return `{${body}}`;
}
